import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BaseResponse } from "api/models/base";
import {
  HiveDetails,
  OrganizationStory,
  OrganizationStoryItem,
  OrganizationStoryObject,
  SegmentItemReactModel,
} from "api/models/community";
import { useAppSelector } from "hooks/redux";
import { title } from "process";

import {
  getHiveDetails,
  getStories,
  getStoryItem,
  getStorySegmentItemReact,
} from "store/async-actions/hive";
import { createTypedSelector } from "store/utils";

interface InitialState {
  hiveDetails: HiveDetails | null;
  storiesList: OrganizationStory[];
  reducedStoryList: OrganizationStoryObject[];
  storyItem: OrganizationStoryItem | null;
  isFetching: boolean;
  hiveUuid: string;
  doesExist: boolean;
  loggedIn: boolean;
  hiveId: number | null;
  startClicked: boolean;
  currentStoryItem: OrganizationStoryItem | null;
  storyUuid: string;
  segmentItemReact: SegmentItemReactModel | undefined;
}

const initialState: InitialState = {
  currentStoryItem: null,
  hiveDetails: null,
  storiesList: [],
  reducedStoryList: [],
  storyItem: null,
  isFetching: true,
  hiveUuid: "",
  doesExist: true,
  loggedIn: false,
  hiveId: null,
  startClicked: false,
  storyUuid: "",
  segmentItemReact: undefined,
};

const hiveSlice = createSlice({
  name: "hive",
  initialState,
  reducers: {
    // setEmoji: (state,action:PayloadAction<>)
    setStartClicked: (state, action: PayloadAction<boolean>) => {
      state.startClicked = action.payload;
    },
    setStoryUuid: (state, action: PayloadAction<string>) => {
      state.storyUuid = action.payload;
    },
    setCurrentStoryItem: (
      state,
      action: PayloadAction<OrganizationStoryItem>
    ) => {
      state.currentStoryItem = action.payload;
    },
  },
  extraReducers: {
    [getHiveDetails.pending.type]: (state) => {
      state.isFetching = true;
    },
    [getHiveDetails.fulfilled.type]: (
      state,
      action: PayloadAction<BaseResponse<HiveDetails>>
    ) => {
      if (action.payload.responseInfo.httpCode === 200) {
        state.hiveDetails = action.payload.data;
        state.hiveUuid = action.payload.data.communityUuid;
        state.doesExist = true;
        state.hiveId = action.payload.data.communityId;
      } else if (action.payload.responseInfo.httpCode === 302) {
        launchLogin();
        state.doesExist = false;
      } else {
        state.doesExist = false;
      }
      state.isFetching = false;
    },
    [getHiveDetails.rejected.type]: (state) => {
      // state.doesExist = false;
      state.isFetching = false;
    },
    [getHiveDetails.pending.type]: (state) => {
      // to do
    },
    [getHiveDetails.rejected.type]: (state) => {
      // to do
      state.doesExist = false;
      state.isFetching = false;
    },
    /**
     *
     * Stories
     */

    [getStories.pending.type]: (state) => {
      state.isFetching = true;
    },
    [getStories.fulfilled.type]: (
      state,
      action: PayloadAction<OrganizationStory[]>
    ) => {
      state.storiesList = action.payload;
      state.reducedStoryList = [];
      state.storiesList.map((story, idx) => {
        state.reducedStoryList.push({
          selectedEmoji: "default",
          storyItemId: story.id,
          storyUuid: story.storyUuid,
          url:
            story.type === "text"
              ? "https://veehivedev-images.s3.amazonaws.com/background/portrait_photo.png"
              : story.storyUrl,
          title: story.title,
          description: story.description,
          type: story.type === "text" ? "image" : story.type,
          actionLink: story.actionLink,
          colorCode: story.colorCode,
          longDescriptionList: story.longDescriptionList,
          textColorCode: story.textColorCode,
        });
      });
      state.isFetching = false;
    },
    [getStories.rejected.type]: (state) => {
      state.isFetching = false;
    },

    //GET STORY SEGMENT REACTIONS
    [getStorySegmentItemReact.pending.type]: (state) => {
      state.isFetching = true;
    },
    [getStorySegmentItemReact.fulfilled.type]: (
      state,
      action: PayloadAction<any>
    ) => {
      state.segmentItemReact = action.payload;
      state.isFetching = false;
    },
    [getStorySegmentItemReact.rejected.type]: (state) => {
      state.isFetching = false;
    },

    /**
     * Story Item
     */

    [getStoryItem.pending.type]: (state) => {
      state.isFetching = true;
    },
    [getStoryItem.fulfilled.type]: (
      state,
      action: PayloadAction<OrganizationStoryItem>
    ) => {
      state.storyItem = action.payload;
      state.isFetching = false;
    },
    [getStoryItem.rejected.type]: (state) => {
      state.isFetching = false;
    },
  },
});

export const launchLogin = () => {
  const redirect = process.env.REACT_APP_LOGIN_URL;

  window.open(
    redirect + `?domain=${localStorage.getItem("subDomain")}&redirect=mobile`,
    "_self"
  );
};

export const { setStartClicked, setCurrentStoryItem, setStoryUuid } =
  hiveSlice.actions;

export const getStartClickedSelector = createTypedSelector(
  (state) => state.hive.startClicked
);

export const getStoryUuidSelector = createTypedSelector(
  (state) => state.hive.storyUuid
);

export const getCurrentStoryItemSelector = createTypedSelector(
  (state) => state.hive.currentStoryItem
);

export const getHiveUuid = createTypedSelector((state) => state.hive.hiveUuid);

export const getHiveSelector = createTypedSelector(
  (state) => state.hive.hiveDetails
);

export const getStoriesListSelector = createTypedSelector(
  (state) => state.hive.reducedStoryList
);

export const getStoryItemSelector = createTypedSelector(
  (state) => state.hive.storyItem
);

export const getStorySegmentItemReactSelector = createTypedSelector(
  (state) => state.hive.segmentItemReact
);

export const getDoesExistSelector = createTypedSelector(
  (state) => state.hive.doesExist
);

export const hiveDetailsLoadingSelector = createTypedSelector(
  (state) => state.hive.isFetching
);

export const getLoggedInSelector = createTypedSelector(
  (state) => state.hive.loggedIn
);

export default hiveSlice.reducer;
